import React, {
  useState, useEffect, useContext, useRef,
} from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements, PaymentElement, useStripe, useElements,
} from '@stripe/react-stripe-js';
import { baseUrl, endpoints, STRIPE_PUBLIC_KEY } from 'Api';
import { AppContext, AuthContext } from 'Context';
import { MEMBERSHIP_PROMO_ENABLED } from 'Utils/settingsAccess';
import styles from './createUserV2.module.css';

const stripePromise = STRIPE_PUBLIC_KEY ? loadStripe(STRIPE_PUBLIC_KEY) : null;
const money = (n) => `$${(Number(n) || 0).toFixed(2)}`;

// In-button spinner (uses the global loading-circle from UserM.css).
const Spinner = () => (
  <svg className={`loading-circle ${styles.buttonSpinner}`} viewBox="0 0 50 50" aria-hidden="true">
    <circle className="circle" cx="25" cy="25" r="20" fill="none" strokeWidth="4" />
  </svg>
);

const CheckRow = ({ on, onToggle, children }) => (
  // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events
  <div className={styles.check} onClick={onToggle}>
    <span className={`${styles.checkBox} ${on ? styles.checkBoxOn : ''}`}>{on ? '✓' : ''}</span>
    <span className={styles.checkText}>{children}</span>
  </div>
);

// Inner form for a NEW card — Stripe Payment Element. Confirms a PaymentIntent
// (paid) or a SetupIntent, then finalizes.
const NewCardForm = ({ payLabel, onConfirmed, isSetup }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState('');

  const pay = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setSubmitting(true); setErr('');
    const opts = { elements, redirect: 'if_required' };
    const { error, paymentIntent, setupIntent } = isSetup
      ? await stripe.confirmSetup(opts)
      : await stripe.confirmPayment(opts);
    if (error) { setErr(error.message || 'Payment could not be completed.'); setSubmitting(false); return; }
    const intent = paymentIntent || setupIntent;
    if (intent && (intent.status === 'succeeded' || intent.status === 'processing')) {
      try {
        await onConfirmed(intent);
      } catch (e2) {
        setErr(e2?.response?.data?.message || 'Could not finalize the subscription.');
        setSubmitting(false);
      }
    } else {
      setErr('Payment was not completed.');
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={pay}>
      <div className={styles.cardBox}><PaymentElement /></div>
      {err && <p className={styles.mError}>{err}</p>}
      <button type="submit" className={styles.mPay} disabled={!stripe || submitting}>
        {submitting ? <Spinner /> : payLabel}
      </button>
    </form>
  );
};

// Payment popup for the create-user flow. Step 1: review + terms + promo. Step 2:
// saved-card vs new-card. $0 skips the card entirely.
const PaymentModalV2 = ({
  open, onClose, plan, user, onPaid, onDoneClose,
}) => {
  const navigate = useNavigate();
  const { getToken } = useContext(AuthContext);
  const { affiliateCode: ctxAffiliateCode, apiResponse } = useContext(AppContext);
  const affiliateCode = apiResponse?.data?.[0]?.affiliateCode || ctxAffiliateCode || '';
  const myToken = getToken();
  const userId = user?.userId || user?.id;

  const [useManagerCard, setUseManagerCard] = useState(false);
  const [terms, setTerms] = useState(false);
  const [promo, setPromo] = useState('');
  const [promoState, setPromoState] = useState('idle'); // idle | checking | valid | invalid
  const [promoMsg, setPromoMsg] = useState('');
  const [promoOff, setPromoOff] = useState(0); // validated discount, in dollars
  const promoDebounce = useRef(null);
  const [managerCards, setManagerCards] = useState([]);
  const [clientSecret, setClientSecret] = useState('');
  const [intentLoading, setIntentLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [step, setStep] = useState('options'); // options | payment | done
  const intentReqRef = useRef(0);

  const full = Number(plan?.amount) || 0;
  const aff = plan?.affiliate;
  const eff = aff ? Number(aff.effective_amount) : full;
  const discount = Math.max(0, full - eff);
  const discounted = discount > 0;
  const promoDollars = promoState === 'valid' ? promoOff : 0;
  const totalDue = Math.max(0, eff - promoDollars);
  const zeroDue = totalDue === 0;
  const amountLabel = money(totalDue);
  const promoBlocks = promo.trim().length > 0 && (promoState === 'checking' || promoState === 'invalid');
  let discLabel = 'Membership discount';
  if (aff?.discount) {
    const d = aff.discount;
    if (d.type === 'percentage' && Number(d.value) === 100) discLabel = 'Membership · Included';
    else if (d.type === 'percentage' && d.value) discLabel = `Membership · ${d.value}% off`;
    else if (d.value) discLabel = `Membership · $${(Number(d.value) / 100).toFixed(2)} off`;
  }

  // Load the manager's saved cards; default to the saved card when one exists.
  useEffect(() => {
    if (!open) return;
    setStep('options'); setError(''); setTerms(false); setBusy(false); setPromo(''); setUseManagerCard(false);
    setPromoState('idle'); setPromoMsg(''); setPromoOff(0);
    axios.post(`${baseUrl}/${endpoints.getMySubscription}`, { token: myToken })
      .then((r) => {
        const cards = Array.isArray(r.data?.paymentMethods) ? r.data.paymentMethods : [];
        setManagerCards(cards);
        setUseManagerCard(cards.length > 0);
      })
      .catch(() => { setManagerCards([]); setUseManagerCard(false); });
  }, [open]);

  // Validate promo (debounced) via getPromoAmount; only when no membership discount.
  useEffect(() => {
    if (discounted) return undefined;
    const code = promo.trim();
    if (!code) { setPromoState('idle'); setPromoMsg(''); setPromoOff(0); return undefined; }
    setPromoState('checking'); setPromoMsg('');
    if (promoDebounce.current) clearTimeout(promoDebounce.current);
    let active = true;
    promoDebounce.current = setTimeout(async () => {
      try {
        const { data } = await axios.post(`${baseUrl}/getPromoAmount`, {
          token: myToken, promo_code: code, plan_id: plan?.id,
        });
        if (!active) return;
        const d = data?.data;
        if (data?.success === false) { setPromoState('invalid'); setPromoMsg(data?.message || 'Invalid promo code'); setPromoOff(0); return; }
        if (d?.limit_exceed) { setPromoState('invalid'); setPromoMsg('Promo code usage limit exceeded.'); setPromoOff(0); return; }
        if (d?.is_expired) { setPromoState('invalid'); setPromoMsg('Promo code has expired.'); setPromoOff(0); return; }
        if (d && d.amount != null) { setPromoState('valid'); setPromoMsg(''); setPromoOff(Number(d.amount) / 100); return; }
        setPromoState('invalid'); setPromoMsg('Invalid promo code'); setPromoOff(0);
      } catch (e) {
        if (!active) return;
        setPromoState('invalid'); setPromoMsg(e?.response?.data?.message || 'Invalid promo code'); setPromoOff(0);
      }
    }, 500);
    return () => { active = false; if (promoDebounce.current) clearTimeout(promoDebounce.current); };
  }, [promo, discounted, plan?.id]);

  // Create a payment intent for the NEW-card path (skipped when $0 or manager card).
  useEffect(() => {
    if (!open || step !== 'payment' || useManagerCard || zeroDue || !userId) { setClientSecret(''); return; }
    const reqId = intentReqRef.current + 1;
    intentReqRef.current = reqId;
    setIntentLoading(true);
    setClientSecret('');
    axios.post(`${baseUrl}/elements/create-payment-intent`, {
      token: myToken, userId, plan_id: plan?.id, promo, trial: false, for_affiliate: true, url: window.location.origin,
    })
      .then((r) => { if (intentReqRef.current === reqId) setClientSecret(r.data?.client_secret || ''); })
      .catch(() => { if (intentReqRef.current === reqId) setError('Could not initialize payment.'); })
      .finally(() => { if (intentReqRef.current === reqId) setIntentLoading(false); });
  }, [open, step, useManagerCard, zeroDue, userId, plan?.id, promo]);

  const finalizeAddPlan = async (checkoutId, managerCardFlag = useManagerCard) => {
    const res = await axios.post(`${baseUrl}/${endpoints.addPlanV2}`, {
      token: myToken,
      userId,
      plan_id: plan?.id,
      is_free: 0,
      for_affiliate: true,
      managerCard: managerCardFlag,
      only_first_year: true,
      promo,
      trial: false,
      ...(checkoutId && { checkout_id: checkoutId }),
    });
    if (res?.data?.success === false) throw new Error(res?.data?.message || 'Could not add plan');
    setStep('done');
    return res;
  };

  const payDirect = async () => {
    setBusy(true); setError('');
    try { await finalizeAddPlan(null); } catch (e) {
      setError(e?.response?.data?.message || e?.message || 'Payment failed. Please try again.');
    } finally { setBusy(false); }
  };

  // $0 due → no card needed: managerCard false, no checkout_id.
  const payNoCard = async () => {
    setBusy(true); setError('');
    try { await finalizeAddPlan(null, false); } catch (e) {
      setError(e?.response?.data?.message || e?.message || 'Could not add plan. Please try again.');
    } finally { setBusy(false); }
  };

  if (!open) return null;
  const canManagerCardOption = managerCards.length > 0;

  // Success screen
  if (step === 'done') {
    return (
      <div className={styles.overlay}>
        <div className={styles.modal} role="dialog" aria-modal="true">
          <div className={styles.confirmWrap}>
            <div className={`${styles.confirmIcon} ${styles.iconOk}`}>✓</div>
            <h3 className={styles.mTitle}>Subscription added</h3>
            <p className={styles.mSub}>{`${plan?.product_name} is now active for this user.`}</p>
            <button type="button" className={styles.mPay} onClick={onPaid}>Access the account</button>
            <button type="button" className={styles.mGhost} onClick={onDoneClose || (() => navigate('/settings/affiliate'))}>Close</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events
    <div className={styles.overlay} onClick={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}>
      <div className={styles.modal} role="dialog" aria-modal="true">
        {step === 'options' ? (
          /* ---- Step 1: review + options ---- */
          <>
            <div className={styles.mHead}>
              <h3 className={styles.mTitle}>Review &amp; options</h3>
              <button type="button" className={styles.mClose} onClick={onClose}>×</button>
            </div>
            <p className={styles.mSub}>{`Subscribe this user to ${plan?.product_name}.`}</p>

            <div className={styles.summary}>
              <div className={styles.sRow}>
                <span className={styles.sLabel}>Plan</span>
                <span className={styles.sVal}>{plan?.product_name}</span>
              </div>
              <div className={styles.sRow}>
                <span className={styles.sLabel}>Regular rate</span>
                <span className={discounted || promoDollars > 0 ? styles.sStrike : styles.sVal}>{money(full)}</span>
              </div>
              {discounted && (
                <div className={styles.sRow}>
                  <span className={styles.sLabel}>{discLabel}</span>
                  <span className={styles.sDiscount}>{`- ${money(discount)}`}</span>
                </div>
              )}
              {promoDollars > 0 && (
                <div className={styles.sRow}>
                  <span className={styles.sLabel}>{`Promo · ${promo.trim()}`}</span>
                  <span className={styles.sDiscount}>{`- ${money(promoDollars)}`}</span>
                </div>
              )}
              <div className={`${styles.sRow} ${styles.sTotal}`}>
                <span className={styles.sTotalLabel}>Total due today</span>
                <span className={styles.sTotalVal}>{money(totalDue)}</span>
              </div>
            </div>

            <CheckRow on={terms} onToggle={() => setTerms((v) => !v)}>
              I agree to the Terms of Use and Subscriber Agreement
            </CheckRow>

            {MEMBERSHIP_PROMO_ENABLED && (
              <>
                <div className={styles.mDivider} />
                <p className={styles.mLabel}>Promo code</p>
                {discounted ? (
                  <div className={styles.promoDisabled}>Not applicable with membership discount</div>
                ) : (
                  <>
                    <input
                      className={`${styles.input} ${styles.promoInput}`}
                      placeholder="Add promo code"
                      value={promo}
                      onChange={(e) => setPromo(e.target.value.toUpperCase())}
                    />
                    {promo.trim() && promoState === 'checking' && <p className={styles.promoNote}>Checking code…</p>}
                    {promo.trim() && promoState === 'valid' && <p className={styles.promoOk}>{`Promo applied — ${money(promoOff)} off`}</p>}
                    {promo.trim() && promoState === 'invalid' && <p className={styles.mError}>{promoMsg || 'Invalid promo code'}</p>}
                  </>
                )}
              </>
            )}
            {affiliateCode ? (
              <>
                <p className={styles.mLabel} style={{ marginTop: 12 }}>Affiliate code</p>
                <input className={styles.input} value={affiliateCode} disabled />
              </>
            ) : null}

            {error && <p className={styles.mError}>{error}</p>}

            <button
              type="button"
              className={styles.mPay}
              disabled={!terms || promoBlocks || busy}
              onClick={() => { setError(''); if (zeroDue) payNoCard(); else setStep('payment'); }}
            >
              {busy ? <Spinner /> : (zeroDue ? 'Continue' : 'Continue to payment')}
            </button>
          </>
        ) : (
          /* ---- Step 2: payment ---- */
          <>
            <div className={styles.mHead}>
              <button type="button" className={styles.mBack} onClick={() => { setError(''); setStep('options'); }} disabled={busy}>← Back</button>
              <button type="button" className={styles.mClose} onClick={onClose} disabled={busy}>×</button>
            </div>
            <h3 className={styles.mTitle}>{zeroDue || useManagerCard ? 'Confirm subscription' : 'Enter card details'}</h3>
            <div className={styles.summary} style={{ marginTop: 12 }}>
              <div className={`${styles.sRow} ${styles.sTotal}`} style={{ borderTop: 'none', marginTop: 0, paddingTop: 0 }}>
                <span className={styles.sTotalLabel}>{plan?.product_name}</span>
                <span className={styles.sTotalVal}>
                  {promoDollars > 0 && <span className={styles.sStrike} style={{ marginRight: 8 }}>{money(eff)}</span>}
                  {money(totalDue)}
                </span>
              </div>
            </div>

            {/* Card choice — saved card (default) vs new card. Only when there's
                an amount due and the manager has a saved card. */}
            {!zeroDue && canManagerCardOption && (
              <div className={styles.payChoices} style={{ marginTop: 14 }}>
                <div
                  role="button"
                  tabIndex={0}
                  onKeyDown={() => {}}
                  onClick={() => { setError(''); setUseManagerCard(true); }}
                  className={`${styles.payChoice} ${useManagerCard ? styles.payChoiceSelected : ''}`}
                >
                  <span className={styles.radio} />
                  <span className={styles.payChoiceText}>
                    {`Pay with my saved card${managerCards[0]?.card?.last4 ? ` (•••• ${managerCards[0].card.last4})` : ''}`}
                  </span>
                </div>
                <div
                  role="button"
                  tabIndex={0}
                  onKeyDown={() => {}}
                  onClick={() => { setError(''); setUseManagerCard(false); }}
                  className={`${styles.payChoice} ${!useManagerCard ? styles.payChoiceSelected : ''}`}
                >
                  <span className={styles.radio} />
                  <span className={styles.payChoiceText}>Pay with New card</span>
                </div>
              </div>
            )}

            {error && <p className={styles.mError}>{error}</p>}

            {/* $0 due → no card needed. */}
            {zeroDue ? (
              <button type="button" className={styles.mPay} onClick={payNoCard} disabled={busy}>
                {busy ? <Spinner /> : 'Continue without card'}
              </button>
            ) : useManagerCard ? (
              <button type="button" className={styles.mPay} onClick={payDirect} disabled={busy}>
                {busy ? <Spinner /> : `Pay ${amountLabel}`}
              </button>
            ) : (
              <div style={{ marginTop: 6 }}>
                {intentLoading && <p className={styles.mSub}>Preparing secure payment…</p>}
                {!intentLoading && clientSecret && stripePromise && (
                  <Elements stripe={stripePromise} options={{ clientSecret, loader: 'never' }}>
                    <NewCardForm
                      payLabel={`Pay ${amountLabel}`}
                      isSetup={clientSecret.startsWith('seti_')}
                      onConfirmed={(intent) => finalizeAddPlan(intent?.id)}
                    />
                  </Elements>
                )}
                {!intentLoading && !clientSecret && stripePromise && (
                  <p className={styles.mError}>Couldn’t start the card form. Please try again.</p>
                )}
                {!stripePromise && (
                  <p className={styles.mError}>Payments are not configured for this environment.</p>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default PaymentModalV2;
